// 介錯「突き当て」(exec_maulJab) の直し方 (2026-10-09 夜の比較版 → 2026-10-10 ユーザー採用「A」で既定 = charge 600ms・DEV ?jabFix=now|0|off で旧)。
//
//   人の記録で、突き当ては出た回のうち半分以上当たっていて、特に「振り下ろし×2→突き当て」
//   (連 exec-maul-drop-drop-jab の3拍目) は5回中4回当たった。4回とも回避を押していない
//   (3回は何も押さず、1回はもう一度攻撃を押した)。900ms の「ワンテンポ」の間に振り下ろしの戻りの絵
//   (overhead_recover・単独の隙と同じ絵) が止まっているので殴りに行き、その振りの最中に
//   溜め 420ms の突きが来る。切り分けと測定 = docs/research/2026-10-09-executioner-maul-jab-fix-options.md。
//
//   ?jabFix= で次の3つを比べた (10-10 に charge を採用 = 指定なしでも charge・600ms・1か所だけ)。拍の数は変えない。now = 前のまま。
//     charge = その3拍目の溜めを 420 → 600ms (&jabFixCharge=500|600|700)。溜めの中身は据えの突き v3
//              (src/exec_brace_thrust.ts) と同じ「切っ先を向けたまま、わずかに沈んで狙う」。当たりの時刻は遅れる。
//              &jabFixScope=pair で「突き当て×2 の2発目」(見切りの後の反撃に刺さる所・溜め 380) にも同じ直しを入れる。
//     early  = 当たりの時刻は今のまま。ワンテンポの最後の 300ms で、振り下ろしの戻りの絵から
//              突きの構え (jab_coil) へ先に替える。溜めの中身は charge と同じ沈み。
//     light  = 時間と絵は今のまま。千秋楽だけ、突き当ての重さを初演と同じ割合へ (体力 16→8・体幹 22→15)。
//
//   ?jabFix= の読み取りは src/game.ts の import.meta.env.DEV の箱の中だけ。配布版ではモードが既定の 'charge' のまま
//   (読み込み時に置く・下の末尾)。
import { getAttack } from './attacks'
import { getBossDefinitionById } from './bosses'
import type { BossAttackTimeline } from './boss_pose_timeline'
import type { Boss, Difficulty } from './types'

export type JabFixMode = 'now' | 'charge' | 'early' | 'light'
export const JAB_FIX_CHARGE_CHOICES = [500, 600, 700] as const
export const JAB_FIX_DEFAULT_CHARGE_MS = 600
// early: ワンテンポ (900ms) の最後のこの長さだけ、先に突きの構えを見せる。
export const JAB_FIX_EARLY_LEAD_MS = 300
// light: 千秋楽 (体力100・体幹100) で、初演 (体力200・体幹150) と同じ割合になる重さ。
//   体力 16/200 = 8% → 8。体幹 22/150 ≒ 14.7% → 15。
export const JAB_FIX_LIGHT = { damage: 8, postureDamage: 15 } as const

const BOSS_ID = 'the-executioner'
const JAB_ID = 'exec_maulJab'
// 直す場所 = 振り下ろし×2→突き当ての3拍目だけ。ほかの連・単発・居座りの応手の突き当ては変えない。
export const JAB_FIX_STRING_ID = 'exec-maul-drop-drop-jab'
export const JAB_FIX_BEAT_INDEX = 2
// charge の時だけ広げられる2か所目 (&jabFixScope=pair) = 突き当て×2 の2発目。
export const JAB_FIX_PAIR_STRING_ID = 'exec-maul-jab-jab'
export const JAB_FIX_PAIR_BEAT_INDEX = 1
export type JabFixScope = 'one' | 'pair'

let mode: JabFixMode = 'now'   // 読み込みの最後に既定の charge へ置く
let scope: JabFixScope = 'one'
// 書き換えた拍と、その前の値 (戻す時に使う)。
let savedCharges: Array<{ beat: { chargeOverrideMs?: number }; ms: number | undefined }> = []
let savedDamage: { damage: number; postureDamage: number } | null = null

export function jabFixMode(): JabFixMode { return mode }
export function jabFixScope(): JabFixScope { return scope }

export function parseJabFixMode(raw: string | null): JabFixMode | null {
  if (raw === 'now' || raw === '0' || raw === 'off') return 'now'   // 前のまま
  return raw === 'charge' || raw === 'early' || raw === 'light' ? raw : null
}
export function parseJabFixCharge(raw: string | null): number {
  const n = Number(raw)
  return (JAB_FIX_CHARGE_CHOICES as readonly number[]).includes(n) ? n : JAB_FIX_DEFAULT_CHARGE_MS
}

// URL の値から切り替える。now|0|off = 前のまま・charge|early|light = その案・それ以外 (指定なし含む) は何もしない = 既定 (charge) のまま。
export function setJabFixFromParams(rawMode: string | null, rawCharge: string | null, rawScope: string | null = null): void {
  const next = parseJabFixMode(rawMode)
  if (next) setJabFixMode(next, parseJabFixCharge(rawCharge), rawScope === 'pair' ? 'pair' : 'one')
}

function beatOf(stringId: string, beatIndex: number) {
  return getBossDefinitionById(BOSS_ID)?.strings?.find((s) => s.id === stringId)?.beats[beatIndex]
}

// scope は charge の時だけ効く (early は 900 の間がある3拍目だけ・light は突き当て全部の重さ)。
export function setJabFixMode(next: JabFixMode, chargeMs = JAB_FIX_DEFAULT_CHARGE_MS, nextScope: JabFixScope = 'one'): void {
  // いったん今の値へ戻してから、次のモードの分だけ書き換える。
  for (const s of savedCharges) s.beat.chargeOverrideMs = s.ms
  savedCharges = []
  restoreDamage()
  mode = next
  scope = next === 'charge' ? nextScope : 'one'
  if (next !== 'charge') return
  const targets = [beatOf(JAB_FIX_STRING_ID, JAB_FIX_BEAT_INDEX)]
  if (scope === 'pair') targets.push(beatOf(JAB_FIX_PAIR_STRING_ID, JAB_FIX_PAIR_BEAT_INDEX))
  for (const beat of targets) {
    if (!beat) continue
    savedCharges.push({ beat, ms: beat.chargeOverrideMs })
    beat.chargeOverrideMs = chargeMs
  }
}

function restoreDamage(): void {
  if (!savedDamage) return
  const atk = getAttack(JAB_ID)
  atk.damage = savedDamage.damage
  atk.postureDamage = savedDamage.postureDamage
  savedDamage = null
}

// light: 毎 tick game.ts (開発版だけ) が今の難易度を渡す。千秋楽の時だけ軽く、それ以外は元の値。
export function syncJabFixDamage(difficulty: Difficulty): void {
  const want = mode === 'light' && difficulty === 'senshuraku'
  if (want && !savedDamage) {
    const atk = getAttack(JAB_ID)
    savedDamage = { damage: atk.damage, postureDamage: atk.postureDamage }
    atk.damage = JAB_FIX_LIGHT.damage
    atk.postureDamage = JAB_FIX_LIGHT.postureDamage
  } else if (!want && savedDamage) {
    restoreDamage()
  }
}

// ===== 絵 (charge / early の時だけ・直す拍だけ) =====
// 今の突き当て (EXEC_JAB_TIMELINE_VARIANTS.lunge) と拍・絵・位置は同じで、溜めの構えが止まる所
//   (0.3 → 終わり) に「わずかに沈んで狙う」を足しただけ。沈みの量は据えの突き v3 と同じ (縦 0.985)。
//   連の2拍目以降なので、頭の idle は comboNoNeutralImage が jab_coil へ差し替える (今と同じ)。
export const EXEC_JAB_FIX_TIMELINE: BossAttackTimeline = [
  { phase: 'telegraph', at: 0.0, image: 'idle', hold: true },
  { phase: 'telegraph', at: 0.2, image: 'idle' },
  { phase: 'telegraph', at: 0.3, image: 'jab_coil', hold: true, tag: 'anticipation',
    ease: 'easeIn', offset: { x: -14 } },
  { phase: 'telegraph', at: 0.995, image: 'jab_coil', offset: { x: -14 }, scale: { x: 1, y: 0.985 } },
  { phase: 'active', at: 0.0, image: 'jab_coil', hold: true, ease: 'easeOut', offset: { x: -14 }, scale: { x: 1, y: 0.985 } },
  { phase: 'active', at: 0.35, image: 'jab_lunge', hold: true, tag: 'contact', offset: { x: 20 } },
  { phase: 'recovery', at: 0.0, image: 'jab_lunge', hold: true, offset: { x: 20 } },
  { phase: 'recovery', at: 0.2, image: 'jab_lunge', hold: true, ease: 'easeIn', offset: { x: 20 } },
  { phase: 'recovery', at: 0.45, image: 'idle', hold: true, ease: 'easeOut' },
]
// early: ワンテンポの最後で先に見せる構え。突きの溜めの頭 (offset 0) と同じ置き方なので、溜めへ入る所で位置は跳ばない。
export const EXEC_JAB_FIX_EARLY_GAP_TIMELINE: BossAttackTimeline = [
  { phase: 'recovery', at: 0.0, image: 'jab_coil', hold: true },
]

function isTargetString(boss: Boss): boolean {
  return boss.id === BOSS_ID && boss.currentString?.id === JAB_FIX_STRING_ID && boss.exec?.held !== 'cannon'
}

/** いま直す拍の突き当て (予兆・当たり・戻り) か。pair の時は突き当て×2 の2発目も。 */
export function isJabFixTargetBeat(boss: Boss): boolean {
  if (boss.id !== BOSS_ID || boss.exec?.held === 'cannon' || boss.currentAttack?.id !== JAB_ID) return false
  if (boss.state !== 'telegraph' && boss.state !== 'active' && boss.state !== 'recovery') return false
  const id = boss.currentString?.id
  if (id === JAB_FIX_STRING_ID) return boss.beatIndex === JAB_FIX_BEAT_INDEX
  return scope === 'pair' && id === JAB_FIX_PAIR_STRING_ID && boss.beatIndex === JAB_FIX_PAIR_BEAT_INDEX
}

/** early: ワンテンポ (2拍目の後の beatGap) の残りが JAB_FIX_EARLY_LEAD_MS 以下か。 */
export function inJabFixEarlyWindow(boss: Boss): boolean {
  if (!isTargetString(boss) || boss.state !== 'beatGap' || boss.beatIndex !== JAB_FIX_BEAT_INDEX - 1) return false
  const beat = boss.currentString!.beats[JAB_FIX_BEAT_INDEX]
  if (!beat || beat.attackId !== JAB_ID) return false
  const gapMs = beat.gapMs ?? 0
  return boss.stateElapsed * 1000 >= gapMs - JAB_FIX_EARLY_LEAD_MS
}

// sprites.ts の bossAttackTimelineOf が最初に聞く。今の版 (配布版) は常に undefined = 何も変えない。
export function jabFixTimeline(boss: Boss): BossAttackTimeline | undefined {
  if (mode !== 'charge' && mode !== 'early') return undefined
  if (isJabFixTargetBeat(boss)) return EXEC_JAB_FIX_TIMELINE
  if (mode === 'early' && inJabFixEarlyWindow(boss)) return EXEC_JAB_FIX_EARLY_GAP_TIMELINE
  return undefined
}

// 既定 (2026-10-10 ユーザー採用) = charge 600ms・1か所 (振り下ろし×2→突き当ての3拍目)。拍の溜めの長さを読み込み時に置く
//   (boss.ts がこのファイルを読むので、ゲームも自動プレイの測定も通る)。
setJabFixMode('charge')
